import React from 'react';
import { productTour } from './builders';

const BACK = { backTo: '/investments/gold', backLabel: 'Back to Gold' };

// Live INR gold price card shown on the Digital Gold and Physical Gold pages.
const goldPriceStep = (usedFor) => ({
  target: '[data-tour="inv-gold-price"]',
  title: "Today's gold price",
  body: (
    <>
      <p>An estimate of today's 24K gold price in India, per 10 g and per gram. It's worked out from the international gold price and the dollar-rupee rate, plus 15% import duty.</p>
      <p>It doesn't include 3% GST, so your jeweller's or app's rate may differ a little. Mickles uses this price {usedFor}. Tap <b>Refresh</b> (the circular arrow) to update it.</p>
    </>
  ),
});

// Product pages under Investments → Gold.
export const GOLD_TOURS = {
  'inv-gold-gold-bonds': productTour({
    name: 'Sovereign Gold Bonds',
    intro: (
      <>
        <p>Sovereign Gold Bonds (SGBs) are issued by the Government of India. Each unit is worth 1 gram of gold, and you also earn <b>2.5% interest a year</b>, paid every six months.</p>
        <p>They run for 8 years, and you can exit early after 5 years. Add your bonds here to track their interest and maturity dates.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Bond Series</b> (for example <b>SGB 2023-24 Series III</b>), the purchase date, the <b>Price</b> you paid per unit (per gram) and the number of <b>Units</b> (grams).</p>
        <p><b>Amount Invested</b> is worked out for you. Bought the same series again? Pick its name from the list so the purchases are grouped together.</p>
      </>
    ),
    live: undefined,
    summary: (
      <p><b>Total</b> is what you paid for all your gold bonds. Interest is shown separately in the table.</p>
    ),
    holdings: (
      <>
        <p>One row per bond series. Dates are counted from your first purchase in that series:</p>
        <ul>
          <li><b>Maturity Date</b>: 8 years after purchase.</li>
          <li><b>Avail for Redemption</b>: when you can exit early (after 5 years).</li>
          <li><b>Interest Earnings</b>: interest earned so far, 1.25% of your investment for every six months completed.</li>
          <li><b>Interest (8 Yrs)</b>: total interest if you hold to maturity.</li>
        </ul>
        <p><b>Click a row</b> to see every purchase, and edit or delete one there.</p>
      </>
    ),
    ...BACK,
  }),

  'inv-gold-gold-etf': productTour({
    name: 'Gold ETFs',
    intro: (
      <>
        <p>Gold ETFs (exchange-traded funds) follow the price of real gold and trade on the stock exchange like a share. You hold them in your demat account, so there's no need to store physical gold.</p>
        <p>Add each purchase here and Mickles tracks what you paid and what it's worth today.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the ETF, the purchase date, the <b>Price</b> per unit and the <b>Number of Units</b>. <b>Amount Invested</b> is worked out for you.</p>
        <p>Type the ETF's <b>NSE symbol</b> as its name (for example <b>GOLDBEES</b>) so live prices can be found. A 5-digit number is treated as a BSE code. Bought more later? Pick the same name from the list.</p>
      </>
    ),
    live: (
      <p>Tap <b>Get Live Price</b> to fetch today's price for each ETF from the exchange. You'll then see its current value and profit or loss. If an ETF shows no price, check that its name is the exchange symbol.</p>
    ),
    ...BACK,
  }),

  'inv-gold-digital-gold': productTour({
    name: 'Digital Gold',
    intro: (
      <>
        <p>Digital gold is real 24K gold you buy in small amounts on an app or website. The provider stores it in a vault for you.</p>
        <p>Add each purchase here and Mickles shows how many grams you own and what they're worth today.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Platform Name</b> (the app you bought on), the purchase date, the <b>Gold Price (per gram)</b> you paid and the <b>Amount Invested</b> in ₹.</p>
        <p>The gold price starts with today's rate, so change it to the rate on your receipt. <b>Units (in grams)</b> is worked out for you (amount ÷ price).</p>
      </>
    ),
    live: undefined,
    summary: (
      <p><b>Invested</b> is what you paid in total. <b>Current Value</b> is your grams × today's gold price, with your gain or loss in brackets.</p>
    ),
    holdings: (
      <>
        <p>One row per platform, combining all your purchases there, with what you paid and its <b>Current Value</b> today.</p>
        <p><b>Click a row</b> to see every purchase, and edit or delete one there. Tap a column heading to sort.</p>
      </>
    ),
    charts: (
      <p>How your digital gold money is split between platforms, by amount invested.</p>
    ),
    extraSteps: [goldPriceStep('to value your digital gold')],
    ...BACK,
  }),

  'inv-gold-gold-mutual-fund': productTour({
    name: 'Gold Mutual Funds',
    intro: (
      <>
        <p>Gold mutual funds invest your money in gold ETFs, so you can own gold without a demat account. You buy units at the fund's NAV (Net Asset Value, the price of one unit that day).</p>
        <p>Add each purchase here and Mickles tracks what you paid and what it's worth today.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Name of Fund</b>, its <b>Symbol Code</b>, the purchase date, the <b>NAV Price</b> and the <b>Amount Invested</b>. <b>Number of Units</b> is worked out for you (amount ÷ NAV).</p>
        <p>The symbol code is needed for live NAVs. Search the fund on Yahoo Finance and copy its code (it looks like <b>0P0000ABCD</b>). Pick an existing fund from the list and its code fills in.</p>
      </>
    ),
    live: (
      <p>Tap <b>Get Live NAV</b> to fetch the latest NAV for each fund, using its symbol code. You'll then see its current value and profit or loss. If a fund shows no NAV, check its symbol code.</p>
    ),
    holdings: (
      <>
        <p>One row per fund, combining all your purchases of it, with what you paid, its current value and your profit or loss.</p>
        <p><b>Click a row</b> to see every purchase, and edit or delete one. Wrong symbol code? Use <b>Edit Symbol</b> there to fix it for all purchases of that fund.</p>
      </>
    ),
    ...BACK,
  }),

  'inv-gold-physical-gold': productTour({
    name: 'Physical Gold',
    intro: (
      <>
        <p>Track the gold you own in person: jewellery, coins and bars.</p>
        <p>Add each item with its weight and Mickles estimates what it's worth at today's gold price.</p>
      </>
    ),
    add: (
      <>
        <p>Enter a <b>Gold Item Description</b> (for example <b>Gold Coin 10g</b>), the purchase date, the <b>Price</b> per gram and the <b>Units</b>. <b>Amount Invested</b> is worked out for you (price × units).</p>
        <p>Enter <b>Units</b> as the <b>weight in grams</b>, not the number of pieces, so the value estimate is right. The price starts with today's rate, so change it to what you paid.</p>
      </>
    ),
    live: undefined,
    summary: (
      <p><b>Invested</b> is what you paid in total. <b>Est. Current Value</b> is your grams × today's 24K gold price. It's a rough guide: 22K jewellery is worth less per gram, and making charges aren't included.</p>
    ),
    holdings: (
      <>
        <p>One row per item, combining all your purchases of it, with what you paid and its <b>Est. Current Value</b>.</p>
        <p><b>Click a row</b> to see every purchase, and edit or delete one there. Tap a column heading to sort.</p>
      </>
    ),
    charts: (
      <p>How your physical gold money is split between items, by amount invested.</p>
    ),
    extraSteps: [goldPriceStep('to estimate the value of your gold')],
    ...BACK,
  }),
};
